import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr, stageLabel, formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { PriorityBadge, StageBadge } from "../components/Badges";
import { ArrowLeft, Activity, CheckCircle2, MapPin, Save } from "lucide-react";
import { toast } from "sonner";
import Attachments from "../components/Attachments";
import Comments from "../components/Comments";

const HEALTH_STYLES = {
  on_track: { label: "On Track", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  at_risk:  { label: "At Risk",  cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  delayed:  { label: "Delayed",  cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  on_hold:  { label: "On Hold",  cls: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  completed:{ label: "Completed", cls: "bg-primary/20 text-primary border-primary/30" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [users, setUsers] = useState([]);
  const [health, setHealth] = useState(null);
  const [handover, setHandover] = useState(null);
  const [visits, setVisits] = useState([]);
  const [signOffName, setSignOffName] = useState("");

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setP(r.data)), [id]);
  const loadHealth = useCallback(() => api.get(`/projects/${id}/health`).then((r) => setHealth(r.data)), [id]);
  const loadHandover = useCallback(() => api.get(`/projects/${id}/handover`).then((r) => {
    setHandover(r.data); setSignOffName(r.data.client_sign_off_name || "");
  }), [id]);
  const loadVisits = useCallback(() => api.get("/site-visits", { params: { project_id: id } }).then((r) => setVisits(r.data)), [id]);

  useEffect(() => {
    load(); loadHealth(); loadHandover(); loadVisits();
    api.get("/users").then((r) => setUsers(r.data));
  }, [load, loadHealth, loadHandover, loadVisits]);

  if (!p) return <div className="text-muted-foreground">Loading…</div>;
  const userName = (uid) => users.find((u) => u.id === uid)?.name || "—";

  const toggleHandoverItem = async (key, checked) => {
    const nextChecklist = {
      ...Object.fromEntries((handover?.items || []).map((i) => [i.key, i.checked])),
      [key]: checked,
    };
    try {
      const { data } = await api.patch(`/projects/${id}/handover`, {
        checklist: nextChecklist, client_sign_off_name: signOffName || undefined
      });
      setHandover(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const saveSignOff = async () => {
    try {
      const checklist = Object.fromEntries((handover?.items || []).map((i) => [i.key, i.checked]));
      const { data } = await api.patch(`/projects/${id}/handover`, { checklist, client_sign_off_name: signOffName });
      setHandover(data);
      toast.success("Sign-off saved");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const healthStyle = health ? HEALTH_STYLES[health.label] : null;

  return (
    <div className="space-y-4">
      <Link to="/projects" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-3 w-3" />Back to projects</Link>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.project_type}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{p.client_name}</h1>
          <div className="text-sm text-muted-foreground mt-1">{p.city} · Owner: {userName(p.owner_id)} · Architect: {p.architect || "—"}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {healthStyle && (
            <Badge className={`${healthStyle.cls} border`} title={health?.reasons?.join(", ") || "All good"} data-testid="project-health-badge">
              <Activity className="h-3 w-3 mr-1" />{healthStyle.label} · {health.score}
            </Badge>
          )}
          <StageBadge label={stageLabel(p.stage)} />
          <PriorityBadge value={p.priority} />
          {p.project_value != null && <div className="font-display text-xl font-bold text-primary">{inr(p.project_value)}</div>}
        </div>
      </div>

      {/* Health reasons */}
      {health?.reasons?.length > 0 && (
        <Card className="p-3 bg-card border-border" data-testid="project-health-reasons">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Attention items</div>
          <div className="flex flex-wrap gap-2">
            {health.reasons.map((r, i) => (
              <span key={i} className="px-2 py-1 rounded border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs">
                {r}
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 bg-card border-border lg:col-span-2">
          <h3 className="font-display font-semibold mb-3">Activity Timeline</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {(p.activity || []).slice().reverse().map((a) => (
              <div key={a.at + (a.user_id || "")} className="flex gap-3">
                <div className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm">{a.text}</div>
                  <div className="text-xs text-muted-foreground font-mono">{new Date(a.at).toLocaleString("en-IN")}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 bg-card border-border">
          <h3 className="font-display font-semibold mb-3">Status</h3>
          <div className="space-y-2 text-sm">
            <Row label="Payment" value={p.payment_status} />
            <Row label="Procurement" value={p.procurement_status} />
            <Row label="Installation" value={p.installation_status} />
            <Row label="Due date" value={p.due_date || "—"} />
            <Row label="Handover date" value={p.handover_date || "—"} />
            <Row label="Next action" value={p.next_action || "—"} />
          </div>
        </Card>
      </div>

      {/* Handover Checklist */}
      {handover && (
        <Card className="p-5 bg-card border-border" data-testid="handover-card">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h3 className="font-display font-semibold flex items-center gap-2">
              <CheckCircle2 className={`h-5 w-5 ${handover.ready ? "text-emerald-400" : "text-muted-foreground"}`} />
              Handover readiness
            </h3>
            <div className="flex items-center gap-3">
              <div className="text-sm">
                <span className="font-mono text-primary">{handover.checked}</span>
                <span className="text-muted-foreground"> / {handover.total} items</span>
              </div>
              {handover.ready && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">Ready</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {handover.items.map((item) => (
              <label key={item.key} className="flex items-center gap-2 p-2 rounded border border-border bg-secondary/30 cursor-pointer hover:border-primary/40 transition-colors"
                     data-testid={`handover-${item.key}`}>
                <input type="checkbox" checked={item.checked}
                       onChange={(e) => toggleHandoverItem(item.key, e.target.checked)} />
                <span className={`text-sm ${item.checked ? "text-foreground" : "text-muted-foreground"}`}>{item.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Client / site manager sign-off name</label>
              <Input value={signOffName} onChange={(e) => setSignOffName(e.target.value)} placeholder="Full name of person signing off" className="mt-1" data-testid="handover-signoff-name" />
            </div>
            <Button onClick={saveSignOff} data-testid="handover-save-signoff"><Save className="h-4 w-4 mr-2" />Save sign-off</Button>
          </div>
        </Card>
      )}

      {/* Site Visits */}
      <Card className="p-5 bg-card border-border" data-testid="project-site-visits">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" />Site Visits</h3>
          <Link to="/site-visits" className="text-xs text-primary hover:underline">Open Site Visits page →</Link>
        </div>
        {visits.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No visits scheduled for this project yet.</div>
        ) : (
          <div className="space-y-2">
            {visits.slice(0, 5).map((v) => (
              <div key={v.id} className="flex items-center justify-between p-2 rounded border border-border bg-secondary/30">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="font-mono text-xs text-muted-foreground w-24 shrink-0">{v.visit_date}</div>
                  <div className="text-sm capitalize">{v.visit_type?.replace(/_/g, " ")}</div>
                  {v.is_outstation && <Badge variant="outline" className="text-[10px]">Outstation</Badge>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="capitalize text-muted-foreground">{v.status?.replace(/_/g, " ")}</span>
                  {v.incentive?.eligible ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">{inr(v.incentive.total_amount)}</Badge>
                  ) : v.incentive?.per_engineer_amount ? (
                    <Badge variant="outline" className="text-[10px]" title={v.incentive.reasons?.join(", ")}>slab {inr(v.incentive.per_engineer_amount)}</Badge>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 bg-card border-border">
        <Attachments kind="project" parentId={id} />
      </Card>

      <Card className="p-5 bg-card border-border">
        <Comments entity="project" parentId={id} />
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-border/60 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium capitalize">{String(value).replace(/_/g, " ")}</span>
    </div>
  );
}
